import './Games.scss';

import { useEffect, useState } from 'react';

import classNames from 'classnames';
import isEmpty from 'lodash/isEmpty';
import { observer } from 'mobx-react';
import { useTranslation } from 'react-i18next';

import { interleaveCategoryBanners } from '../../features/categoryBanners/interleaveCategoryBanners';
import { ONBOARDING_CONFIG } from '../../features/onboarding/constants';
import { GamesOnboarding } from '../../features/onboarding/GamesOnboarding';
import { useCategoryBannersQuery } from '../../hooks/queries/useCategoryBannersQuery';
import { useSelectCategory } from '../../hooks/useSelectCategory';
import { DEFAULT_LANGUAGE } from '../../i18n/constants';
import { getUserLanguage } from '../../i18n/helpers';
import { useFlag } from '../../services/launchDarkly';
import { useStores } from '../../stores/useStores';
import { useTooltip } from '../Tooltip/useTooltip';

import { GamesAutoCarousel } from './AutoCarousel/GamesAutoCarousel';
import { CAROUSEL_GAMES_TYPE, NUMBERED_GAMES_TYPE } from './constants';
import { GamesCarousel } from './GamesCarousel';
import { GamesInfiniteCarousel } from './GamesInfiniteCarousel';
import { useGamesSeen } from './hooks/useGamesSeen';
import { useImpressionView } from './hooks/useImpressionView';
import { isJackpotCategory } from './JackpotCategoryHeader/isJackpotCategory';
import { JackpotCategoryHeader } from './JackpotCategoryHeader/JackpotCategoryHeader';
import { LazyCategoryRow } from './LazyCategoryRow';
import GamesLoader from './Loader';
import { NumberedGamesCarousel } from './NumberedGamesCarousel/NumberedGamesCarousel';
import { seenGamesLobbyListener } from './seenGamesImpression';
import Title from './Title';

const EAGER_ROW_COUNT = 2;

interface GamesProps {
  carouselClassName?: string;
  carouselLeftArrowClassName?: string;
  carouselRightArrowClassName?: string;
}

export const Games = observer((props: GamesProps) => {
  const { carouselClassName, carouselRightArrowClassName, carouselLeftArrowClassName } = props;
  const { t, i18n } = useTranslation();
  const language = getUserLanguage(i18n.language);
  const { categoriesStore, gamesSearchStore, jackpotStore, onboardingStore } = useStores();
  const { selectCategory } = useSelectCategory();
  const { tooltipStyles, setReferenceElement, setPopperElement } = useTooltip('top', [-20, 20]);

  const [isTooltipReady, setIsTooltipReady] = useState(false);

  const showOnboarding = onboardingStore.step === ONBOARDING_CONFIG.START_GAME;

  const isInfiniteCarouselEnabled = useFlag('web_infinite_carousel', true);
  const isNumberedGamesEnabled = useFlag('numbered_games', false);
  const isCarouselCategoryEnabled = useFlag('carousel_category', false);

  const { data: categoryBanners } = useCategoryBannersQuery(language);

  useImpressionView('home', 'all');
  useGamesSeen(seenGamesLobbyListener);

  useEffect(() => {
    setTimeout(() => {
      if (showOnboarding) {
        setIsTooltipReady(true);
      }
    }, 300);
  }, [showOnboarding]);

  if (categoriesStore.loading.get('categories')) {
    return <GamesLoader />;
  }

  // Counts only rows that actually render, so onboarding lands on the first *visible* category
  // even when an earlier one is empty or a hidden jackpot row.
  let visibleCount = 0;
  // Search sub-category is filtered out (not mapped to null) so `idx` — data-row-num and the eager-row
  // cutoff — keeps main's semantics.
  const categoryElements = categoriesStore.categoriesIds
    .filter((id) => id !== gamesSearchStore.gameSubCategorySearchId)
    .map((id, idx) => {
      const category = categoriesStore.categoriesRegistry[id];
      if (!category || isEmpty(category.subCategoryGames)) {
        return null;
      }
      const { subCategoryGames: games } = category;

      /**
       * The jackpot collection is hidden ENTIRELY unless the player can see the jackpot — feature
       * flag off, no enabled jackpot for this coin, no access, or a failed lookup all drop the
       * whole row rather than leaving a Crown Jackpot collection with no jackpot attached to it.
       *
       * Previously this only swapped the header, so the games still listed under a standard
       * category title. That reads as an ordinary collection, which is worse than absent: the
       * games are only grouped because of a jackpot the player is not in.
       */
      const isJackpotRow = isJackpotCategory(category, jackpotStore.eligibleCategoryIds);

      if (isJackpotRow && !jackpotStore.canShowCategoryRow) {
        return null;
      }

      const isFirstVisibleRow = visibleCount++ === 0;
      const onboardingRow = showOnboarding && isFirstVisibleRow;
      const showJackpotHeader = isJackpotRow;

      // A carousel category owns the whole row: it loops through every game it holds, so there
      // is no truncated tail for "See all" to open.
      const isAutoCarousel = category.carouselType === CAROUSEL_GAMES_TYPE && isCarouselCategoryEnabled;
      return (
        <div
          key={id}
          className={classNames('games', { games_onboarding: onboardingRow })}
          data-category-id={id}
          data-games-row={category.name[DEFAULT_LANGUAGE]}
          data-row-num={idx}
        >
          <LazyCategoryRow eager={idx < EAGER_ROW_COUNT || onboardingRow || categoriesStore.eagerCategoryIds.has(id)}>
            {showJackpotHeader ? (
              <JackpotCategoryHeader
                categoryId={id}
                hideSeeAll={isAutoCarousel}
                name={category.name[language] || category.name[DEFAULT_LANGUAGE]}
              />
            ) : (
              <div className="games__category-wrapper">
                <Title name={category.name[language] || category.name[DEFAULT_LANGUAGE]} />
                {(category.carouselType !== NUMBERED_GAMES_TYPE || !isNumberedGamesEnabled) && !isAutoCarousel && (
                  <button className="games__see-all" onClick={() => selectCategory(id)}>
                    {t('common.seeAll')}
                  </button>
                )}
              </div>
            )}

            {isAutoCarousel ? (
              <GamesAutoCarousel
                carouselClassName={carouselClassName}
                carouselLeftArrowClassName={carouselLeftArrowClassName}
                carouselRightArrowClassName={carouselRightArrowClassName}
                id={id}
                onboardingRow={onboardingRow}
                setReferenceElement={setReferenceElement}
              />
            ) : category.carouselType === NUMBERED_GAMES_TYPE && isNumberedGamesEnabled ? (
              <NumberedGamesCarousel
                carouselClassName={carouselClassName}
                carouselLeftArrowClassName={carouselLeftArrowClassName}
                carouselRightArrowClassName={carouselRightArrowClassName}
                id={id}
              />
            ) : isInfiniteCarouselEnabled ? (
              <GamesInfiniteCarousel
                carouselClassName={carouselClassName}
                carouselLeftArrowClassName={carouselLeftArrowClassName}
                carouselRightArrowClassName={carouselRightArrowClassName}
                id={id}
                onboardingRow={onboardingRow}
                setReferenceElement={setReferenceElement}
              />
            ) : (
              <GamesCarousel id={id} onboardingRow={onboardingRow} setReferenceElement={setReferenceElement} />
            )}
            {onboardingRow && isTooltipReady && (
              <GamesOnboarding game={games[1]} setPopperElement={setPopperElement} tooltipStyles={tooltipStyles} />
            )}
          </LazyCategoryRow>
        </div>
      );
    });

  // Same gate as every Crown Jackpot lobby surface: hidden while it's off, loading or in maintenance.
  const visibleCategoryBanners = categoryBanners?.filter(
    (banner) => banner.ctaType !== 'CROWN_JACKPOT' || jackpotStore.canShowWidget,
  );

  return <>{interleaveCategoryBanners(categoryElements, visibleCategoryBanners)}</>;
});
